import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import { formatCurrency } from '../utils/helpers';
import ActivityForm from '../components/forms/ActivityForm';
import ContactForm from '../components/forms/ContactForm';
import BaseModal from '../components/modals/BaseModal';
import ActivityTimeline from '../components/common/ActivityTimeline';

const ContactDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    activities,
    deals,
    integrations,
    updateContact: updateLegacyContact
  } = useCRM();
  const { contacts, companies } = useCRMData();
  const [activeTab, setActiveTab] = useState('timeline');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [noteText, setNoteText] = useState('');

  const contact = contacts.find(c => c.contact_id === id);
  const company = companies.find(c =>
    (c.company_id === contact?.company_id || c.id === contact?.company_id)
  );
  const linkedInIntegration = integrations.find(i => i.name === 'LinkedIn');
  const contactActivities = activities.filter(a => a.contact_id === id).sort((a, b) => new Date(b.date) - new Date(a.date));
  const contactDeals = deals.filter(d => d.contact_id === id);

  useEffect(() => {
    if (!contact && contacts.length > 0) {
      navigate('/contacts');
    }
  }, [contact, contacts, navigate]);

  if (!contact) return null;

  const phone = contact.phone || contact.mobile || contact.phone_number || '';
  const email = contact.email || contact.epost || '';

  const handleSaveNote = () => {
    if (!noteText.trim()) return;
    const newNote = `[Notat ${new Date().toLocaleDateString()}]: ${noteText}`;
    updateLegacyContact(contact.contact_id, { notes: contact.notes ? `${contact.notes}\n\n${newNote}` : newNote });
    setNoteText('');
  };

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center">
              <button onClick={() => navigate('/contacts')} className="mr-4 p-2 hover:bg-gray-100 rounded-full text-gray-500">
                <SafeIcon icon="⬅️" className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                  {getInitials(contact.name)}
                </div>
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-gray-900 flex items-center">
                    {contact.name}
                    <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {contact.role_in_buying}
                    </span>
                  </h1>
                  <p className="text-sm text-gray-500 flex items-center">
                    {company?.name} • <span className="ml-1 text-gray-400">Sist kontaktet: {new Date().toLocaleDateString()}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 shadow-sm flex items-center">
                <SafeIcon icon="✅" className="w-4 h-4 mr-2" /> Vunnet
              </button>
              <button className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 shadow-sm flex items-center">
                <SafeIcon icon="❌" className="w-4 h-4 mr-2" /> Tapt
              </button>
            </div>
          </div>
          <div className="flex space-x-8 mt-2 overflow-x-auto">
            {[
              { id: 'timeline', label: 'Tidslinje' },
              { id: 'activities', label: 'Aktiviteter' },
              { id: 'notes', label: 'Notater' },
              { id: 'deals', label: 'Salgsmuligheter' },
              { id: 'files', label: 'Filer' },
              { id: 'documents', label: 'Dokumenter ✨' },
              { id: 'emails', label: 'E-post' },
              { id: 'linkedin', label: 'LinkedIn', icon: '👔' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors capitalize whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                {tab.icon && <SafeIcon icon={tab.icon} className="w-4 h-4" />}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT SIDEBAR */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Kontaktinfo</h3>
                <button onClick={() => setShowEditModal(true)} className="text-blue-600 text-sm hover:underline">Oppdater</button>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <SafeIcon icon="💼" className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <label className="block text-xs text-gray-500">Tittel</label>
                    <div className="text-sm font-medium text-gray-900">{contact.title}</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <SafeIcon icon="📞" className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <label className="block text-xs text-gray-500">Mobil</label>
                    <a href={`tel:${phone}`} className="text-sm font-medium text-blue-600 hover:underline">{phone}</a>
                  </div>
                </div>
                <div className="flex items-start">
                  <SafeIcon icon="📧" className="w-5 h-5 text-gray-400" />
                  <div>
                    <label className="block text-xs text-gray-500">E-post</label>
                    <a href={`mailto:${email}`} className="text-sm font-medium text-blue-600 hover:underline break-all">{email}</a>
                  </div>
                </div>
                {company?.website && (
                  <div className="flex items-start">
                    <SafeIcon icon="🌐" className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                    <div>
                      <label className="block text-xs text-gray-500">Nettside</label>
                      <a href={company.website} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline">{company.website}</a>
                    </div>
                  </div>
                )}
                {contact.linkedin && (
                  <div className="flex items-start">
                    <SafeIcon icon="👔" className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                    <div>
                      <label className="block text-xs text-gray-500">LinkedIn</label>
                      <a href={contact.linkedin} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline">Vis profil</a>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Relasjonsstyrke</h3>
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Poeng</span>
                    <span>{contact.relation_strength}/5</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${contact.relation_strength >= 4 ? 'bg-green-500' : contact.relation_strength >= 3 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${(contact.relation_strength / 5) * 100}%` }}></div>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center">
                  Etiketter <SafeIcon icon="🏷️" className="ml-2 text-gray-400 w-3 h-3" />
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full border border-gray-200">Kunde</span>
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full border border-blue-100">VIP</span>
                  <button className="px-2 py-1 text-xs text-blue-600 border border-dashed border-blue-300 rounded-full hover:border-blue-400 hover:bg-blue-50">
                    + Legg til tag
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* MAIN CONTENT */}
          <div className="lg:col-span-2">
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center px-2">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight italic">Aktivitets-tidslinje</h3>
                  <button onClick={() => setShowActivityModal(true)} className="text-sm font-bold text-blue-600 hover:underline">
                    + Logg aktivitet
                  </button>
                </div>
                <ActivityTimeline activities={contactActivities} />
              </div>
            )}

            {activeTab === 'activities' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    <button className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-md border border-blue-200 whitespace-nowrap">Gjøremål</button>
                    <button className="px-3 py-1 text-xs font-medium bg-white text-gray-600 rounded-md border border-gray-200 hover:bg-gray-50 whitespace-nowrap">Forfalt</button>
                    <button className="px-3 py-1 text-xs font-medium bg-white text-gray-600 rounded-md border border-gray-200 hover:bg-gray-50 whitespace-nowrap">Ferdig</button>
                  </div>
                  <button onClick={() => setShowActivityModal(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center shadow-sm whitespace-nowrap ml-2">
                    Ny aktivitet
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[400px]">
                  {contactActivities.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {contactActivities.map(activity => (
                        <div key={activity.activity_id} className="p-4 hover:bg-gray-50 transition-colors flex items-start group">
                          <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${activity.status === 'Gjennomført' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                            <SafeIcon icon="⚡" className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{activity.title}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{activity.description}</p>
                            <div className="flex items-center mt-2 text-xs text-gray-400 space-x-3">
                              <span className="flex items-center">
                                <SafeIcon icon="📅" className="w-3 h-3 mr-1" />
                                {new Date(activity.date).toLocaleDateString()}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${activity.status === 'Gjennomført' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {activity.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                      <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-4">
                        <SafeIcon icon="✅" className="w-10 h-10 text-green-500" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">Ingen aktiviteter planlagt</h3>
                      <p className="text-gray-500 max-w-xs mx-auto mt-2 text-sm">Vi skulle ønske ting skjedde av seg selv, men den gang ei... vi prøvde virkelig... det går bare ikke :/</p>
                      <button onClick={() => setShowActivityModal(true)} className="mt-6 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
                        Opprett ny aktivitet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <textarea
                    className="w-full bg-transparent border-0 focus:ring-0 text-gray-800 placeholder-gray-400 resize-none outline-none"
                    rows="3"
                    placeholder="Skriv et notat..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                  />
                  <div className="flex justify-end mt-2">
                    <button onClick={handleSaveNote} disabled={!noteText.trim()} className="px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 disabled:opacity-50 disabled:hover:bg-blue-500 shadow-sm">
                      Lagre
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Historikk</h3>
                  {contact.notes ? (
                    <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
                      {contact.notes}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Ingen notater enda.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'deals' && (
              <div className="space-y-4">
                {contactDeals.map(deal => (
                  <div key={deal.deal_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-blue-600">{deal.title}</h4>
                        <p className="text-sm text-gray-500">Forventet lukking: {deal.expected_close_date}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-gray-900">{formatCurrency(deal.value)}</div>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mt-1 ${deal.stage_id.includes('6') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {deal.probability}% Sannsynlighet
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {contactDeals.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <p className="text-gray-500">Ingen avtaler knyttet til denne kontakten.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'linkedin' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {!linkedInIntegration?.connected ? (
                  <div className="p-12 text-center">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon="👔" className="w-10 h-10 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Koble til LinkedIn</h3>
                    <p className="text-gray-500 max-w-md mx-auto mb-8">
                      Få dypere innsikt i dine kontakter ved å koble CRM til LinkedIn. Se siste poster, jobboppdateringer og felles forbindelser.
                    </p>
                    <button onClick={() => navigate('/integrations')} className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all">
                      Gå til Integrasjoner
                    </button>
                  </div>
                ) : !contact.linkedin ? (
                  <div className="p-12 text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon="👤" className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Ingen LinkedIn-profil</h3>
                    <p className="text-gray-500 mb-6">Vi fant ingen LinkedIn-URL for denne kontakten. Legg til URL for å se innsikt.</p>
                    <button className="text-blue-600 font-medium hover:underline">
                      + Legg til LinkedIn URL
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    <div className="p-6 bg-slate-50 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white p-1 rounded-full shadow-sm">
                          <img src={`https://ui-avatars.com/api/?name=${contact.name}&background=0D8ABC&color=fff`} alt={contact.name} className="w-full h-full rounded-full object-cover" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{contact.name}</h3>
                          <p className="text-sm text-gray-500">{contact.title} at {company?.name}</p>
                          <p className="text-xs text-gray-400 mt-1">Oslo-området, Norge</p>
                        </div>
                      </div>
                      <a href={contact.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#0077b5] text-white rounded-lg font-bold text-sm hover:bg-[#006396] transition-colors">
                        <SafeIcon icon="↗️" className="w-4 h-4" /> Åpne i LinkedIn
                      </a>
                    </div>
                    <div className="p-6">
                      <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Siste aktivitet</h4>
                      <div className="space-y-6">
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <SafeIcon icon="🗨️" className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-900 font-medium">Likte et innlegg fra <span className="text-blue-600 font-bold">NHO</span></p>
                            <p className="text-xs text-gray-400 mt-1">2 timer siden</p>
                            <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 text-sm text-gray-600 italic">
                              "Viktig diskusjon om fremtidens energibehov i Norge. Vi må satse nå!"
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <BaseModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Oppdater kontakt" disableAnimation>
        <ContactForm contact={contact} onClose={() => setShowEditModal(false)} />
      </BaseModal>

      <BaseModal isOpen={showActivityModal} onClose={() => setShowActivityModal(false)} title="Ny aktivitet">
        <ActivityForm onClose={() => setShowActivityModal(false)} initialData={{ contact_id: contact.contact_id, company_id: contact.company_id }} />
      </BaseModal>
    </div>
  );
};

export default ContactDetails;